import { computed, inject, Injectable } from '@angular/core';
import { CommonGridObject, GridInput } from '@it2go/data-grid';
import { ObjectDb, OFFLINE_DB_TOKEN } from './object.db';
import { isOffline } from '../../utils/connection.service';
import {
    DB_COMMON_CACHE_TABLE,
    DB_DELAYED_REQUEST_TABLE,
    DB_NAME,
    DB_SCHEMA_TABLE,
    DB_VERSION,
    DB_WYSIWYG_DATA_TABLE,
} from './db-types';
import { FilterBuilder } from '../../modules/content/helper/filter.builder';
import { preparePromise } from '../../utils/promise.utils';
import { DelayedDb } from './delayed-db/delayed-db';
import { CommonCacheDb } from './common.cache-db';
import { currentUserId } from '../current-user-id';
import { WysiwygDb } from './wysiwyg-db/wysiwyg-db';

@Injectable({
    providedIn: 'root',
})
export class OfflineDb {

    public delayedDb = inject(DelayedDb);
    public wysiwygDb = inject(WysiwygDb);

    protected dbs = inject<ObjectDb<any>[]>(OFFLINE_DB_TOKEN);
    protected commonCache = inject(CommonCacheDb);

    public isDownloading = computed(() => {
        return !isOffline() && this.dbs.some((it) => it.isDownloading());
    });

    protected redirectQueries: Record<string, string> = {
        'folder.getByPerson': 'folder',
    };

    constructor() {
        this.initialize();
    }

    public async refreshAllDbs(clear: boolean = true): Promise<void> {
        if (clear) await this.clearAllDbs();
        await Promise.all([
            ...this.dbs.map((it) => it.refresh()),
        ]);
    }

    public async clearAllDbs(ignoreOnUserAgnostic: boolean = false): Promise<void> {
        await Promise.all([
            ...this.dbs.map((it) => it.clear(ignoreOnUserAgnostic)),
            this.commonCache.clear(),
        ]);
    }

    private async initialize(): Promise<void> {
        let iDb: IDBDatabase;
        let isNewVersion = false;
        const [promise, resolve, reject] = preparePromise();

        const request = indexedDB.open(DB_NAME, DB_VERSION);
        request.onerror = (event: any) => {
            const message = event.target.error?.message || '';
            console.error(`Indexed database error: ${message}`);
            // 'The requested version (1) is less than the existing version (31)';
            if (message.includes(`The requested version (${DB_VERSION}) is less than the existing version`)) {
                indexedDB.deleteDatabase(DB_NAME);
                this.initialize();
            }

            reject(event);
        };

        request.onsuccess = (event: any) => {
            iDb = event.target.result;
            this.commonCache.setDatabase(iDb);
            this.delayedDb.setDatabase(iDb);
            this.wysiwygDb.setDatabase(iDb);
            this.dbs.forEach((it) => it.setDatabase(iDb));

            if (isNewVersion) {
                const store = iDb.transaction(DB_SCHEMA_TABLE, 'readwrite').objectStore(DB_SCHEMA_TABLE);
                const tables = [
                    DB_COMMON_CACHE_TABLE,
                    DB_DELAYED_REQUEST_TABLE,
                    DB_WYSIWYG_DATA_TABLE,
                    ...this.dbs.map((it) => it.tableName),
                ];
                tables.forEach((name) => {
                    store.put({
                        name,
                        value: null,
                        userId: currentUserId(),
                    });
                });
            }
            resolve(event);
        };

        request.onupgradeneeded = (event: any) => {
            iDb = event.target.result;
            isNewVersion = true; // Schema creation cannot be run within this event

            for (let i = iDb.objectStoreNames.length - 1; i >= 0; i--) {
                iDb.deleteObjectStore(iDb.objectStoreNames.item(i) || '');
            }

            iDb.createObjectStore(DB_SCHEMA_TABLE, { keyPath: 'name' });
            this.commonCache.initializeStore(iDb);
            this.delayedDb.initializeStore(iDb);
            this.wysiwygDb.initializeStore(iDb);
            this.dbs.forEach((it) => it.initializeStore(iDb));
        };

        await promise;
    }

    public async get<T>(resource: string, method: string, input?: any): Promise<T | null> {
        const table = this.redirectQueries[`${resource}.${method}`];
        if (table) {
            return this.redirectQuery(table, input);
        } else {
            return this.commonCache.getStored(resource, method);
        }
    }

    public async store(resource: string, method: string, result: any): Promise<void> {
        await this.commonCache.store(resource, method, result);
    }

    public async delay(resource: string, method: string, input: any): Promise<void> {
        await this.delayedDb.store(resource, method, input);
    }

    public async filter<T>(table: string, input: GridInput): Promise<CommonGridObject> {
        const db = this.getDb<T>(table);
        if (!db) {
            return { items: [] };
        }

        return db.filter(input);
    }

    private async redirectQuery(table: string, input: any = {}): Promise<any> {
        const filter = new FilterBuilder();
        Object.entries(input).forEach(([key, value]) => {
            filter.where(key, value);
        });

        const result = await this.filter(table, filter.filter());

        return result?.items?.[0] || null;
    }

    private getDb<T>(name: string): ObjectDb<T> | null {
        return this.dbs.find((it) => it.tableName == name) || null;
    }

}
